export type CultureDimension = 'clan' | 'adhocracy' | 'market' | 'hierarchy';

export interface CultureQuestion {
  id: string;
  text: string;
  dimension: CultureDimension;
  forCandidate: boolean;
  candidateText?: string;
}

export const CULTURE_QUESTIONS: CultureQuestion[] = [
  // ─── Clan (Colaboración) ───
  {
    id: 'cul_c1',
    dimension: 'clan',
    forCandidate: true,
    text: 'En esta empresa, nos tratamos más como un equipo o familia que como simples colegas.',
    candidateText: 'Prefiero trabajar en un lugar donde nos tratemos como un equipo unificado, más que como simples colegas.',
  },
  { id: 'cul_c2', dimension: 'clan', forCandidate: false, text: 'El trabajo en equipo y la colaboración se valoran por encima de la competencia individual.' },
  {
    id: 'cul_c3',
    dimension: 'clan',
    forCandidate: true,
    text: 'La dirección se preocupa genuinamente por el bienestar y desarrollo personal de los empleados.',
    candidateText: 'Valoro mucho que los líderes se preocupen genuinamente por mi bienestar y desarrollo personal.',
  },
  { id: 'cul_c4', dimension: 'clan', forCandidate: false, text: 'Existe un alto nivel de confianza y cohesión interna entre los miembros.' },
  {
    id: 'cul_c5',
    dimension: 'clan',
    forCandidate: true,
    text: 'Se fomenta activamente el consenso y la participación en la toma de decisiones.',
    candidateText: 'Me gusta que se fomente activamente el consenso y la participación en la toma de decisiones.',
  },

  // ─── Adocracia (Innovación) ───
  {
    id: 'cul_i1',
    dimension: 'adhocracy',
    forCandidate: true,
    text: 'Se nos motiva a proponer ideas nuevas, incluso si cambian la forma tradicional de trabajar.',
    candidateText: 'Me motiva un entorno donde se me impulse a proponer ideas nuevas y romper con lo tradicional.',
  },
  {
    id: 'cul_i2',
    dimension: 'adhocracy',
    forCandidate: true,
    text: 'La empresa reacciona rápidamente ante los cambios del mercado o nuevas oportunidades.',
    candidateText: 'Prefiero formar parte de una organización ágil que reacciona rápidamente ante nuevas oportunidades.',
  },
  { id: 'cul_i3', dimension: 'adhocracy', forCandidate: false, text: 'Aquí se valora la iniciativa personal y la capacidad de aprender de los errores.' },
  { id: 'cul_i4', dimension: 'adhocracy', forCandidate: false, text: 'El ambiente es emprendedor y dinámico, permitiendo tomar riesgos calculados.' },
  {
    id: 'cul_i5',
    dimension: 'adhocracy',
    forCandidate: true,
    text: 'Se incentiva la experimentación constante para resolver problemas complejos.',
    candidateText: 'Disfruto los lugares donde se incentiva la experimentación y la creatividad para resolver problemas complejos.',
  },

  // ─── Mercado (Resultados) ───
  {
    id: 'cul_a1',
    dimension: 'market',
    forCandidate: true,
    text: 'La prioridad número uno de todos es alcanzar los objetivos y metas de negocio.',
    candidateText: 'Para mí, la prioridad principal en el trabajo debe ser alcanzar objetivos y metas de negocio.',
  },
  { id: 'cul_a2', dimension: 'market', forCandidate: false, text: 'Somos muy exigentes con los estándares de éxito y nos comparamos con la competencia.' },
  {
    id: 'cul_a3',
    dimension: 'market',
    forCandidate: true,
    text: 'El reconocimiento está directamente ligado al cumplimiento de resultados medibles.',
    candidateText: 'Busco que mi esfuerzo y reconocimiento estén directamente ligados al cumplimiento de resultados medibles.',
  },
  {
    id: 'cul_a4',
    dimension: 'market',
    forCandidate: true,
    text: 'La empresa premia la capacidad de ganar y destacar en entornos competitivos.',
    candidateText: 'Me atrae un ambiente exigente que premie la capacidad de ganar y destacar frente a la competencia.',
  },
  { id: 'cul_a5', dimension: 'market', forCandidate: false, text: 'Existe una fuerte orientación hacia la satisfacción del cliente y la excelencia externa.' },

  // ─── Jerarquía (Control) ───
  {
    id: 'cul_s1',
    dimension: 'hierarchy',
    forCandidate: true,
    text: 'Existen procedimientos claros y reglas establecidas para la mayoría de las actividades.',
    candidateText: 'Prefiero entornos con procedimientos muy claros y reglas establecidas para todas las actividades.',
  },
  { id: 'cul_s2', dimension: 'hierarchy', forCandidate: false, text: 'La eficiencia operativa y el control de los procesos son fundamentales en el día a día.' },
  {
    id: 'cul_s3',
    dimension: 'hierarchy',
    forCandidate: true,
    text: 'Se espera que todos respeten las líneas de mando y las jerarquías establecidas.',
    candidateText: 'Considero importante que en el trabajo se respeten estrictamente las líneas de mando y jerarquías.',
  },
  {
    id: 'cul_s4',
    dimension: 'hierarchy',
    forCandidate: true,
    text: 'El orden, la estabilidad y la predictibilidad son valores muy apreciados.',
    candidateText: 'El orden, la estabilidad y la predictibilidad son valores que aprecio mucho en mi lugar de trabajo.',
  },
  { id: 'cul_s5', dimension: 'hierarchy', forCandidate: false, text: 'El éxito se entiende como el cumplimiento estricto de estándares y políticas.' },
];

export interface CultureBlock {
  id: string;
  title: string;
  items: readonly string[];
}

/** Bloques de la encuesta ipsativa (100 puntos por bloque entre 4 ítems, una por dimensión). */
export const CULTURE_BLOCKS: CultureBlock[] = [
  { id: 'block_1', title: '1. Enfoque General y Ambiente', items: ['cul_c1', 'cul_i1', 'cul_a1', 'cul_s1'] },
  { id: 'block_2', title: '2. Estilo de Trabajo y Prioridades', items: ['cul_c2', 'cul_i2', 'cul_a2', 'cul_s2'] },
  { id: 'block_3', title: '3. Liderazgo y Reconocimiento', items: ['cul_c3', 'cul_i3', 'cul_a3', 'cul_s3'] },
  { id: 'block_4', title: '4. Dinámica y Cohesión', items: ['cul_c4', 'cul_i4', 'cul_a4', 'cul_s4'] },
  { id: 'block_5', title: '5. Criterios de Éxito y Metas', items: ['cul_c5', 'cul_i5', 'cul_a5', 'cul_s5'] },
];
